import { createContext, type ReactNode, useContext } from 'react'
import cx from 'classnames'
import { m, type Variants } from 'motion/react'
import styles from './GlobalNavigationBar.module.css'
import { Link, NavLink, type NavLinkRenderProps } from 'react-router-dom'
import { Icon, type IconName } from '@/components/Icon'
import { mergeProps, useFocusRing, useHover } from 'react-aria'
import { match } from '@/utils/pattern-match-util'
import { useElementSize } from '@/hooks/element-hooks'
import { clamp } from 'lodash-es'
import { isHoverSupported } from '@/utils/browser-util'

export interface GlobalNavigationBarProps {
  /**
   * Page content.
   */
  children?: ReactNode

  /**
   * Whether a page navigation is pending
   */
  isNavigating?: boolean
}

/**
 * The global navigation of the app.
 */
export function Root({ isNavigating, children }: GlobalNavigationBarProps) {
  return (
    <IsNavigatingContext value={isNavigating ?? false}>
      <div className={cx(styles.root)}>{children}</div>
    </IsNavigatingContext>
  )
}

interface LogoProps {
  src: string
  alt: string
  to: string
  testId?: string
}

/**
 * The logo part of the navbar. Use inside the list.
 */
export function Logo({ src, alt, to, testId }: LogoProps) {
  const { focusProps, isFocusVisible } = useFocusRing({})
  return (
    <li className={styles.logo}>
      <Link
        data-test={testId}
        to={to}
        className={cx(styles.logoLink, isFocusVisible && styles.focusVisible)}
        {...focusProps}
      >
        <img className={styles.logoImage} src={src} alt={alt} />
      </Link>
    </li>
  )
}

interface ListProps {
  children?: ReactNode
  /**
   * If `true`, when the {@link Root}'s {@link GlobalNavigationBarProps.isNavigating}
   * is `true`, a navigation indicator will be displayed in the navbar on smaller
   * viewports.
   */
  isNavigationIndicator?: boolean
}

/**
 * The list of navigation items (the navbar itself).
 */
export function List({ isNavigationIndicator, children }: ListProps) {
  const isNavigating = useContext(IsNavigatingContext)
  const { connectRef, size } = useElementSize()

  // Ensures the "path" looks similar regardless of width.
  // For example, on a larger screen, we'll need to reduce the range
  // of the angle to preserve the same visual look.
  const degreeModifier = clamp((size.width - 325) / 30, 0, 100)
  const start = 145 - degreeModifier
  const end = 360 - start
  const midpoint = 180
  const isAnimating = isNavigationIndicator && isNavigating

  return (
    <m.div
      ref={connectRef}
      className={styles.navbarBorder}
      animate={{
        '--conic-nav-indicator-opacity': isAnimating ? '100%' : '0%',
        '--conic-nav-indicator-spread': isAnimating ? '0%' : '80%',
        '--conic-nav-indicator-angle': isAnimating
          ? [`${start}deg`, `${end}deg`, `${start}deg`]
          : `${midpoint}deg`,
        transition: {
          '--conic-nav-indicator-angle': {
            duration: isAnimating ? 1.5 : 0.5,
            repeat: isAnimating ? Infinity : 0,
            ease: 'circInOut',
          },
          '--conic-nav-indicator-opacity': {
            duration: isAnimating ? 0.2 : 0.5,
            delay: isAnimating ? 0 : 0.7,
          },
          '--conic-nav-indicator-spread': {
            duration: isAnimating ? 0 : 0.7,
            delay: isAnimating ? 0 : 0.1,
            ease: 'circOut',
          },
        },
      }}
    >
      <nav className={styles.navbar}>
        <ul className={cx(styles.navigation)}>{children}</ul>
      </nav>
    </m.div>
  )
}

export interface ItemProps {
  /**
   * Where to navigate to.
   */
  to: string
  /**
   * The label of the link.
   */
  label: string
  /**
   * Default icon.
   */
  icon: IconName
  /**
   * Icon to use when active.
   */
  iconActive: IconName
  /**
   * Displays a badge when specified and uses the text as an accessible label.
   */
  badgeLabel?: string
  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Variants for the badge animation.
 */
const itemBadgeAnimationVariants: Variants = {
  visible: {
    opacity: 1,
    scale: 1,
  },
  hidden: {
    opacity: 0,
    scale: 0.7,
  },
}

/**
 * Items in the navbar.
 */
export function Item({
  to,
  label,
  icon,
  iconActive,
  badgeLabel,
  testId,
}: ItemProps) {
  const { hoverProps, isHovered: _isHovered } = useHover({})
  const isHovered = _isHovered && isHoverSupported
  const { focusProps, isFocusVisible } = useFocusRing({})

  const navLinkClassNames = ({ isActive }: NavLinkRenderProps) =>
    cx(
      styles.itemLink,
      isActive && styles.active,
      isHovered && styles.hover,
      isFocusVisible && styles.focusVisible,
    )

  return (
    <li className={styles.item}>
      <NavLink
        data-test={testId}
        to={to}
        className={navLinkClassNames}
        {...mergeProps(hoverProps, focusProps)}
      >
        {({ isActive }) => (
          <>
            <div className={styles.icon}>
              <Icon name={isActive ? iconActive : icon} />
              <m.svg
                width="11"
                height="12"
                viewBox="0 0 11 12"
                fill="none"
                initial={false}
                variants={itemBadgeAnimationVariants}
                transition={{ type: 'spring', duration: 0.5 }}
                animate={badgeLabel ? 'visible' : 'hidden'}
                className={cx(styles.badge)}
                {...(badgeLabel && {
                  role: 'status',
                  'aria-label': badgeLabel,
                })}
              >
                <circle cx="5.5" cy="6" r="4.7" strokeWidth="1.4" />
              </m.svg>
            </div>
            <div className={styles.label}>{label}</div>
          </>
        )}
      </NavLink>
    </li>
  )
}

/**
 * Adds spacing between items when necessary.
 */
export function Spacer() {
  return <div className={styles.spacer} />
}

interface ViewportProps {
  children?: ReactNode
  background?: 'primary' | 'secondary'
  /**
   * If `true`, when the {@link Root}'s {@link GlobalNavigationBarProps.isNavigating}
   * is `true`, a navigation indicator will be displayed at the top of the viewport.
   */
  isNavigationIndicator?: boolean
}

/**
 * The viewport, which renders the content governed by the navbar.
 */
export function Viewport({
  children,
  isNavigationIndicator,
  background = 'primary',
}: ViewportProps) {
  const isNavigating = useContext(IsNavigatingContext)

  return (
    <div
      className={cx(
        styles.viewport,
        match(background, {
          primary: styles.primary,
          secondary: styles.secondary,
        }),
      )}
    >
      {isNavigationIndicator && (
        <m.div
          aria-hidden
          className={cx(styles.viewportNavigationIndicator)}
          initial={isNavigating}
          animate={{
            '--radial-spread': isNavigating ? '0%' : '100%',
            '--radial-position': isNavigating ? ['0%', '100%', '0%'] : '50%',
            opacity: isNavigating ? 1 : [1, 0],
            transition: {
              '--radial-spread': { duration: isNavigating ? 0 : 0.25 },
              '--radial-position': {
                duration: 1.05,
                repeat: isNavigating ? Infinity : 0,
                ease: 'linear',
              },
              opacity: {
                duration: isNavigating ? 0.1 : 0.2,
                delay: isNavigating ? 0 : 0.3,
              },
            },
          }}
        />
      )}

      {children}
    </div>
  )
}

/**
 * Context for propagating the {@link GlobalNavigationBarProps.isNavigating}
 * value to child components.
 */
const IsNavigatingContext = createContext(false)
